import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  HeartIcon,
  ChatBubbleLeftIcon,
  ShareIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { PortraitFrame } from '../common/editorial/EditorialControls';
import UserAvatar from '../common/UserAvatar';
import CommentSection from './CommentSection';

const TOPIC_LABELS = {
  achievements: 'Achievement',
  investing: 'Investing',
  budgeting: 'Budgeting',
  goals: 'Goals',
  market: 'Market',
  advice: 'Advice',
};

const PostCard = ({ post, user, onLike, onComment }) => {
  const [showComments, setShowComments] = useState(false);
  const [isLiking, setIsLiking] = useState(false);

  const handleLike = async () => {
    if (isLiking) return;
    setIsLiking(true);
    try {
      await onLike();
    } finally {
      setIsLiking(false);
    }
  };

  const authorInitials = post.author?.name?.charAt(0)?.toUpperCase() || 'U';

  return (
    <article className="surface-panel p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          {post.author.is_verified_advisor ? (
            <PortraitFrame
              size="xs"
              src={post.author.avatar_url || post.author.avatar}
              alt={post.author.name}
              initials={authorInitials}
            />
          ) : (
            <UserAvatar
              userId={post.author.user_id}
              src={post.author.avatar_url || post.author.avatar}
              alt={post.author.name}
              size="xs"
            />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-body font-medium text-primary">{post.author.name}</h3>
              {post.author.verified && (
                <CheckBadgeIcon className="w-4 h-4 text-pine shrink-0" aria-label="Verified" />
              )}
            </div>
            <p className="text-label text-faint">{post.timestamp}</p>
          </div>
        </div>

        {post.topic && (
          <span className="topic-tag shrink-0">
            {TOPIC_LABELS[post.topic] || post.topic}
          </span>
        )}
      </div>

      <div className="mb-5">
        <p className="text-body text-primary leading-relaxed whitespace-pre-wrap">
          {post.content}
        </p>
      </div>

      <div className="flex items-center gap-1 pt-4 border-t border-hairline">
        <button
          type="button"
          onClick={handleLike}
          disabled={isLiking}
          className={`icon-btn ${post.liked ? 'icon-btn-active' : ''}`}
          aria-pressed={post.liked}
        >
          {post.liked ? (
            <HeartIconSolid className="w-5 h-5" />
          ) : (
            <HeartIcon className="w-5 h-5" />
          )}
          <span className="text-label figure tabular-nums">{post.likes}</span>
        </button>

        <button
          type="button"
          onClick={() => setShowComments(!showComments)}
          className={`icon-btn ${showComments ? 'icon-btn-active' : ''}`}
          aria-expanded={showComments}
        >
          <ChatBubbleLeftIcon className="w-5 h-5" />
          <span className="text-label figure tabular-nums">{post.comments}</span>
        </button>

        <button type="button" className="icon-btn" aria-label="Share">
          <ShareIcon className="w-5 h-5" />
          <span className="text-label">Share</span>
        </button>
      </div>

      {showComments && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="mt-5 pt-5 border-t border-hairline"
        >
          <CommentSection
            key={post.id}
            postId={post.id}
            user={user}
            onComment={onComment}
            postTopic={post.topic}
            postContent={post.content}
          />
        </motion.div>
      )}
    </article>
  );
};

export default PostCard;
